import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import _ from 'lodash';
import { goBack, push } from 'connected-react-router';
import { withRouter } from 'react-router';

import { PageHeader } from '../../components/PageHeader';
import Strings from '../../Strings';
import { actions } from './redux/actions';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { TextInput } from '../../components/PageHeader/TextInput';
import {
  toolTipForTable,
  downloadFileOnly,
  signalRender,
  convertUnixEpochToHumanReadable,
} from '../../utils';
import Select from '../../components/Select';
import {
  isCapManager,
  isOrganizationManager,
  isShipper,
  isSuperUser,
  isOmron,
} from '../../utils/userRoles';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import { openModalAction } from '../../actions/modal';
import {
  FEATURE_CONFIGURATION_RESULT,
  DOWNLOAD_REPORT_DATA_ERROR,
  SHIPPER_CAPS_RESULT,
} from './redux/constants';
import { SEND_SECURITY_CODE_CAP } from '../../actions/action-types';
import {
  ActionWidget,
  ActionButton,
  ActionDropdownOnOffMenu,
  ActionDropdownSingleChoiceMenu,
  ItemSelectorHelper,
} from '../../components/ActionWidget';
import { notificationActions } from '../../components/Notification/redux/actions';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersForRequest, urlInjected } from '../../components/AdvancedFilters/helpers';
import { getFiltersDefinition } from '../../components/AdvancedFilters/FiltersDefinition';
import { DateFilters } from '../../utils/DateFilters';
import { DeviceTypes } from '../../utils/deviceTypes';
import { BatteryWithChargingStatus } from '../../components/ChargingStatus/ChargingStatus';
import OrgSelect, { NO_ORG } from '../../components/OrgSelect/OrgSelect';
import { useGlobalPageSize } from '../../utils/tableUtils';

function Hub(props) {
  const [hubs, setHubs] = React.useState([]);
  const [pagination, setPagination] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [openActionWidget, setOpenActionWidget] = React.useState(false);
  const [multiselectedItems, setMultiselectedItems] = React.useState(false);
  const [keyForItemCheckboxes, setKeyForItemCheckboxes] = React.useState(false);

  const globalPageSize = useGlobalPageSize();
  const hasFiltersInUrl = urlInjected(window.location.search);

  const controllerRef = React.useRef(undefined);
  const loadingRef = React.useRef(false);
  const refreshEnabled = React.useRef(true);
  const itemSelectorHelper = React.useRef(new ItemSelectorHelper()).current;
  const hasSeenHydratedFilters = React.useRef(false);

  const request = React.useRef({
    limit: globalPageSize,
    offset: 0,
    search: '',
    filters: [],
    orgId: props.orgIds.filter(id => id !== NO_ORG).length ? props.orgIds.filter(id => id !== NO_ORG) : undefined,
    status: props.orgIds.includes(NO_ORG) ? 'unassigned' : undefined,
  });

  const resetActionWidget = React.useCallback(() => {
    setKeyForItemCheckboxes(prev => !prev);
    setOpenActionWidget(false);
    itemSelectorHelper.clearItems();
  }, [itemSelectorHelper]);

  const getCaps = React.useCallback(() => {
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    request.current.deviceType = DeviceTypes.Datahub.id;

    const controller = new AbortController();
    controllerRef.current = controller;
    setIsLoading(true);
    props.getCaps(request.current, controller.signal).then(resp => {
      loadingRef.current = false;
      if (resp?.type === SHIPPER_CAPS_RESULT) {
        setHubs(resp.response?.data);
        setPagination(resp.response?.pagination);
        resetActionWidget();
      }
      setIsLoading(false);
    });
  }, [props.getCaps, resetActionWidget]);

  const load = React.useCallback(() => {
    getCaps();
    resetActionWidget();
  }, [getCaps, resetActionWidget]);

  const prevOrgIdRef = React.useRef(props.orgIds);
  const prevFiltersRef = React.useRef(props.filters);
  const isInitialMount = React.useRef(true);

  React.useEffect(() => {
    // Initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (props.history?.location?.state?.params?.offset) {
        request.current.offset = props.history?.location?.state?.params?.offset;
      }
      if (!urlInjected(window.location.search)) {
        getCaps();
      }
      return () => {
        if (controllerRef.current) {
          props.cancelGetCaps(controllerRef.current);
        }
      };
    }

    //   changed
    if (props.orgIds !== prevOrgIdRef.current) {
      prevOrgIdRef.current = props.orgIds;
      request.current.orgId = props.orgIds.filter(id => id !== NO_ORG).length ? props.orgIds.filter(id => id !== NO_ORG) : undefined;
      request.current.offset = 0;
      if (props.orgIds.includes(NO_ORG)) {
        delete request.current.orgId;
        request.current.status = 'unassigned';
      } else {
        delete request.current.status;
      }
      load();
      return;
    }

    // filters changed
    if (props.filters !== prevFiltersRef.current) {
      if (hasFiltersInUrl && !hasSeenHydratedFilters.current && props.filters.length === 0) {
        return;
      }
      if (props.filters.length > 0) {
        hasSeenHydratedFilters.current = true;
      }
      prevFiltersRef.current = props.filters;
      onFiltersChange(props.filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orgIds, props.filters]);

  const onRefresh = React.useCallback(() => {
    if (refreshEnabled.current) {
      refreshEnabled.current = false;
      setTimeout(() => {
        refreshEnabled.current = true;
      }, 1000);
      request.current.offset = 0;
      load();
    }
  }, [load]);

  const onSortClick = React.useCallback((sorting) => {
    if (sorting && sorting.length > 0) {
      request.current.offset = 0;
      request.current.sortColumn = sorting[0].id;
      request.current.sortType = sorting[0].desc ? 'descending' : 'ascending';
      load();
    }
  }, [load]);

  const onRowSelection = React.useCallback((row) => {
    props.onNavigate(`/devices/${DeviceTypes.Datahub.id}/${row.cap_id}/devices`, { offset: request.current.offset });
  }, [props.onNavigate]);

  const onOffsetChange = React.useCallback((offset) => {
    request.current.offset = offset;
    load();
  }, [load]);

  const onPageSizeChange = React.useCallback((newSize) => {
    request.current.offset = 0;
    request.current.limit = newSize;
    load();
  }, [load]);

  const onTextInputChange = React.useCallback((e) => {
    onSearchQueryChange(e.target.value);
  }, []);

  const onSearchQueryChangeDebounced = React.useMemo(() => _.debounce(query => {
    request.current.offset = 0;
    request.current.search = query;
    load();
  }, 1000), [load]);

  const onSearchQueryChange = React.useCallback((query) => {
    return onSearchQueryChangeDebounced(query);
  }, [onSearchQueryChangeDebounced]);

  const filtersDebounced = React.useMemo(() => _.debounce(() => {
    const newFiltersForRequest = getFiltersForRequest(props.filters);
    if (
      JSON.stringify(newFiltersForRequest) !== props.filters &&
      JSON.stringify(newFiltersForRequest) !== JSON.stringify(request.current.filters)
    ) {
      request.current.filters = newFiltersForRequest;
      request.current.offset = 0;
    }
    load();
  }, 1000), [props.filters, load]);

  const onFiltersChange = React.useCallback((filters) => {
    return filtersDebounced(filters);
  }, [filtersDebounced]);

  const onDownload = React.useCallback(() => {
    const downloadHandler = params => {
      const paramsWithType = params;
      paramsWithType.deviceType = DeviceTypes.Datahub.id;

      switch (params.selectedOption) {
        case 'events':
          delete paramsWithType.selectedOption;
          props.downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
          break;
        case 'details':
          delete paramsWithType.selectedOption;
          paramsWithType.limit = 100000;
          paramsWithType.offset = 0;
          props.downloadDetails(params).then(onDownloadBtnClickSuccessHandler);
          break;
        default:
          props.downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
      }
      props.showNotification('Download request sent, please wait...');
    };
    const downloadRequest = { ...request.current };
    delete downloadRequest.offset;
    delete downloadRequest.limit;
    if (!downloadRequest.lastUpdatedStart) {
      downloadRequest.lastUpdatedStart = DateFilters.AllTime.dates.startDate();
    }
    if (!downloadRequest.lastUpdatedEnd) {
      downloadRequest.lastUpdatedEnd = DateFilters.AllTime.dates.endDate();
    }

    let orgNames = '';
    if (downloadRequest.orgIds) {
      orgNames = props.orgs?.filter(o => downloadRequest.orgIds.includes(o.masked_id))?.map(o => o.name).join('_') || '';
      if (downloadRequest.orgIds.includes(NO_ORG)) {
        orgNames = Strings.patientDashboard.noOrg;
      }
    }
    props.openDateRangeSelectionModal(downloadHandler, downloadRequest, orgNames);
  }, [props.downloadEvents, props.downloadDetails, props.showNotification, props.openDateRangeSelectionModal, props.orgs]);

  const onDownloadBtnClickSuccessHandler = React.useCallback((req) => {
    if (req?.type === DOWNLOAD_REPORT_DATA_ERROR) {
      props.showNotification(req?.error);
    } else {
      const receivedName = req.headers['content-disposition'].split('filename=')[1];
      const fileName = `${moment().toString() + receivedName}`;
      downloadFileOnly(req, fileName);
    }
  }, [props.showNotification]);

  const onSleepModeChange = React.useCallback((state) => {
    let message = 'Sleep mode disabled';
    if (state === 1) {
      message = 'Sleep mode enabled';
    }

    const req = {
      devices: itemSelectorHelper.getItems(),
      features: [
        {
          name: 'sleep',
          int_value: state,
        },
      ],
    };
    props.showNotification('Processing, please wait...');
    props.featureConfiguration(req).then(resp => {
      if (resp.type === FEATURE_CONFIGURATION_RESULT) {
        props.showNotification(message);
        getCaps();
      } else {
        props.showNotification('Error');
      }
    });
  }, [itemSelectorHelper, props.showNotification, props.featureConfiguration, getCaps]);

  const onPairingModeChange = React.useCallback((value) => {
    const message = `Pairing mode set`;

    const req = {
      devices: itemSelectorHelper.getItems(),
      features: [
        {
          name: 'pairing',
          int_value: value,
        },
      ],
    };
    props.showNotification('Processing, please wait...');
    props.featureConfiguration(req).then(resp => {
      if (resp.type === FEATURE_CONFIGURATION_RESULT) {
        props.showNotification(message);
        getCaps();
      } else {
        props.showNotification('Error');
      }
    });
  }, [itemSelectorHelper, props.showNotification, props.featureConfiguration, getCaps]);

  const onResetBle = React.useCallback(() => {
    const req = {
      devices: itemSelectorHelper.getItems(),
      features: [
        {
          name: 'cmd',
          string_value: 'bleTalker reset',
        },
      ],
    };

    const data = {
      title: <span>{Strings.resetBleWarning}</span>,
      onConfirmAction: actions.featureConfiguration(req),
      onCancelAction: null,
      confirmPostAction: getCaps,
      hideCaution: true,
    };
    props.openConfirmModal(data);
  }, [itemSelectorHelper, props.openConfirmModal, getCaps]);

  const onDateRangeChanged = React.useCallback((option) => {
    if (option) {
      request.current.lastUpdatedStart = option.dates.startDate();
      request.current.lastUpdatedEnd = option.dates.endDate();
    } else {
      request.current.lastUpdatedStart = '';
      request.current.lastUpdatedEnd = '';
    }
    request.current.offset = 0;
    load();
  }, [load]);

  const remindersRender = React.useCallback((value) => {
    if (value == true) {
      return (
        <div className="reminders-container no-border">
          <div className="reminders on" />
        </div>
      );
    }
    return (
      <div className="reminders-container">
        <div className="reminders off" />
      </div>
    );
  }, []);

  const sleepModeRender = React.useCallback((value) => {
    if (value == true) return Strings.on;
    return Strings.off;
  }, []);

  const pairingModeRender = React.useCallback((value) => {
    if (value == 1) return Strings.factory;
    else if (value == 2) return Strings.customer;
    return Strings.off;
  }, []);

  const cityDisplay = React.useCallback((value) => {
    const location = value.split('"');
    const index = location.findIndex(e => e == 'city');
    if (index != -1) {
      return location[index + 2];
    }
    return '-';
  }, []);

  const onCapsDelete = React.useCallback((event, cap_id) => {
    event.preventDefault();
    event.stopPropagation();

    const selected_device = hubs.filter(hub => hub.cap_id === cap_id)?.pop();

    const req = {
      organization_id: selected_device?.organization?.masked_id,
      device_id: cap_id,
    };

    if (isCapManager() || isOrganizationManager()) {
      const data = {
        title: (
          <span>
            {Strings.deleteCapWarning} <b>{cap_id}</b>?
          </span>
        ),
        onConfirmAction: actions.deleteCapManagerCaps(req),
        confirmPostAction: getCaps,
        onCancelAction: null,
      };
      props.openConfirmModal(data);
    } else {
      const data = {
        title: (
          <span>
            {Strings.deleteCapWarning} <b>{cap_id}</b> from organization <b>{selected_device?.organization?.name}</b>?
          </span>
        ),
        onConfirmAction: actions.deleteCaps(req),
        onCancelAction: null,
        confirmPostAction: getCaps,
      };
      props.openConfirmModal(data);
    }
  }, [hubs, props.openConfirmModal, getCaps]);

  const itemChecked = React.useCallback(({ target }) => {
    if (target.checked === true) {
      itemSelectorHelper.addItem(target.id);
    } else {
      itemSelectorHelper.removeItem(target.id);
    }

    if (itemSelectorHelper.getItems().length !== 0) {
      setOpenActionWidget(true);
    } else {
      setOpenActionWidget(false);
    }

    setMultiselectedItems(itemSelectorHelper.isMultiselect());
  }, [itemSelectorHelper]);

  const onCapsSendPin = React.useCallback((event, cap_id) => {
    event.preventDefault();
    event.stopPropagation();

    const data = {
      onConfirmAction: actions.sendCapsPin,
      actionType: SEND_SECURITY_CODE_CAP,
      onCancelAction: null,
      capId: cap_id,
      postAction: getCaps,
    };
    props.openSendPinModal(data);
  }, [props.openSendPinModal, getCaps]);

  const onReminderChange = React.useCallback((state) => {
    let reminderTimeout = 0;
    let message = 'Reminder disabled';
    if (state === 1) {
      reminderTimeout = 24;
      message = 'Reminder enabled';
    }

    const req = {
      devices: itemSelectorHelper.getItems(),
      features: [
        {
          name: 'reminderTimeout',
          int_value: reminderTimeout,
        },
      ],
    };
    props.showNotification('Processing, please wait...');
    props.featureConfiguration(req).then(resp => {
      if (resp.type === FEATURE_CONFIGURATION_RESULT) {
        props.showNotification(message);
        getCaps();
      } else {
        props.showNotification('Error');
      }
    });
  }, [itemSelectorHelper, props.showNotification, props.featureConfiguration, getCaps]);

  const statusRender = React.useCallback((status, ble_reset) => {
    const content = (
      <div className="status-with-info">
        {status || '-'}
        {ble_reset ? <div className="signal-alert info" /> : <React.Fragment />}
      </div>
    );

    const tooltip = Strings.awaitingBleReset;
    return ble_reset ? toolTipForTable(content, tooltip) : content;
  }, []);

  const getHeaderComponents = () => {
    const { onAttachDevice } = props;

    return (
      <>
        <OrgSelect addNoOrg />
        <Button class="refresh" onClick={onRefresh} />
        <TextInput className="search" placeholder={Strings.search} onChange={onTextInputChange} />
        <Select
          data={[
            DateFilters.AllTime,
            DateFilters.Last48Hours,
            DateFilters.ThisMonth,
            DateFilters.Last30Days,
            DateFilters.LastMonth,
            DateFilters.Last6Months,
            DateFilters.ThisYear,
            DateFilters.Last12Months,
          ]}
          onChange={onDateRangeChanged}
          defaultValue={DateFilters.AllTime.value}
        />
        <AdvancedFilters lastButton>
          <Filter definition={getFiltersDefinition().Status} />
          <Filter definition={getFiltersDefinition().Sample} />
          <Filter definition={getFiltersDefinition().Reminder} />
          <Filter definition={getFiltersDefinition().Signal} />
          <Filter definition={getFiltersDefinition().Battery} />
          <Filter definition={getFiltersDefinition().Faults} />
        </AdvancedFilters>
        <Button class="download" onClick={onDownload} />
        {(isShipper() || isSuperUser()) && (
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            onClick={() => onAttachDevice(getCaps)}
            title={Strings.attachCap}
          />
        )}
      </>
    );
  };

  const columns = React.useMemo(() => {
    const cols = [
      columnHelper.accessor('cap_id', {
        header: Strings.deviceId,
        cell: info => (
          <React.Fragment>
            <div className="cell-with-select">
              <div className="selector" onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="item-checkbox"
                  id={info.getValue()}
                  onChange={itemChecked}
                  key={keyForItemCheckboxes}
                />
              </div>
              <div className="selector-label">{info.getValue()}</div>
            </div>
          </React.Fragment>
        ),
        enableSorting: true,
        size: 200,
      }),
      columnHelper.accessor('cap_name', {
        header: Strings.deviceName,
        cell: info => info.getValue() || '-',
        enableSorting: false,
      }),
      columnHelper.accessor('imei', {
        header: Strings.imei,
        cell: info => info.getValue() || '-',
        enableSorting: false,
      }),
      columnHelper.accessor('kit_id', {
        header: Strings.kitId,
        cell: info => info.getValue() || '-',
        enableSorting: false,
      }),
      columnHelper.accessor('is_sample', {
        header: Strings.deviceInvoices.sample,
        cell: info => (info.getValue() ? Strings.yes : Strings.no),
        enableSorting: false,
      }),
    ];

    if (props.orgs?.length > 1) {
      cols.push(
        columnHelper.accessor('organizationName', {
          id: 'organization',
          header: Strings.organizationName,
          accessorFn: row => row.organization?.name || '-',
          enableSorting: false,
        }),
      );
    }

    cols.push(
      columnHelper.accessor('status', {
        header: Strings.status,
        cell: info => statusRender(info.getValue(), info.row.original.ble_reset),
        enableSorting: false,
      }),
      columnHelper.accessor('signal_strength', {
        header: Strings.signal,
        cell: info => signalRender(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor('sleep_mode', {
        header: Strings.sleepMode,
        cell: info => sleepModeRender(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor('pairing', {
        header: Strings.pairingMode,
        cell: info => pairingModeRender(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor('reminder', {
        header: Strings.reminders,
        cell: info => remindersRender(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor('battery', {
        header: Strings.battery.battery,
        cell: info => (
          <BatteryWithChargingStatus
            battery={info.getValue()}
            batteryMeasuredAt={info.row.original.battery_measured_at}
            chargingStatus={info.row.original.charging_status}
          />
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('firmware91', {
        id: 'fota',
        header: Strings.fota,
        cell: info => info.getValue() || null,
        enableSorting: true,
      }),
      columnHelper.accessor('pins', {
        header: Strings.pins,
        cell: info => info.getValue() || null,
        enableSorting: false,
      }),
      columnHelper.accessor('devices', {
        header: Strings.devices,
        cell: info => (info.getValue() ? toolTipForTable(info.getValue(), info.row.original.deviceIds) : 0),
        enableSorting: false,
      }),
      columnHelper.accessor('readings', {
        header: Strings.readings,
        enableSorting: false,
      }),
      columnHelper.accessor('location', {
        header: Strings.location,
        cell: info => (info.getValue() ? cityDisplay(info.getValue()) : '-'),
        enableSorting: true,
      }),
      columnHelper.accessor('last_updated_at', {
        header: Strings.lastActivity,
        cell: info => (info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-'),
        enableSorting: true,
      }),
      columnHelper.accessor('last_activity_type', {
        header: Strings.activityType,
        cell: info => info.getValue() || '-',
        enableSorting: false,
      }),
    );

    return cols;
  }, [props.orgs?.length, keyForItemCheckboxes, itemChecked, statusRender, sleepModeRender, pairingModeRender, remindersRender, cityDisplay]);

  const actionBtns = (
    <>
      <ActionDropdownOnOffMenu
        img="action-sleepmode"
        tooltiptext={Strings.sleepMode}
        off={{label: Strings.formatString(Strings.actionOptionOff, Strings.sleepMode)}}
        on={{label: Strings.formatString(Strings.actionOptionOn, Strings.sleepMode)}}
        action={value => {
          onSleepModeChange(value);
        }}
      />
      <ActionDropdownSingleChoiceMenu
        img="action-pairingmode"
        tooltiptext={Strings.pairingMode}
        options={[
          {
            value: 0,
            text: Strings.off,
          },
          {
            value: 1,
            text: Strings.factoryMode,
          },
          {
            value: 2,
            text: Strings.customerMode,
          },
        ]}
        action={value => {
          onPairingModeChange(value);
        }}
      />
      <ActionDropdownOnOffMenu
        img="action-reminder"
        tooltiptext={Strings.reminder}
        off={{label: Strings.formatString(Strings.actionOptionOff, Strings.reminder)}}
        on={{label: Strings.formatString(Strings.actionOptionOn, Strings.reminder)}}
        action={value => {
          onReminderChange(value);
        }}
      />
      <ActionButton
        img="action-pinset"
        tooltiptext="Set PIN"
        text="Set PIN"
        action={e => {
          onCapsSendPin(e, itemSelectorHelper.getFirstItem());
        }}
        disabled={multiselectedItems}
      />
      <ActionButton
        img="action-reset"
        tooltiptext={Strings.resetBle}
        text={Strings.resetBle}
        action={onResetBle}
      />
      {!isOmron() && !isCapManager() ? (
        <ActionButton
          img="action-delete"
          tooltiptext="Delete"
          text="Delete"
          action={e => {
            onCapsDelete(e, itemSelectorHelper.getFirstItem());
          }}
          disabled={multiselectedItems}
        />
      ) : null}
    </>
  );

  const headerTitle =
    !isLoading && hubs && pagination
      ? `${Strings.showingXHubs.showing} ${hubs.length} ${Strings.showingXHubs.of} ${
          pagination.totalRecords ? pagination.totalRecords : 0
        } ${Strings.showingXHubs.hubs}`
      : Strings.showingWait;

  return (
    <div className="tab-page-content">
      <PageHeader
        isBlack
        left={headerTitle}
        right={getHeaderComponents()}
      />
      <AdvancedFiltersBar />
      <Table3
        name="hub"
        data={hubs}
        columns={columns}
        onRowClick={onRowSelection}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortClick}
        pagination={pagination}
        isLoading={isLoading}
        enableColumnFiltering
      />
      <ActionWidget show={openActionWidget}>{actionBtns}</ActionWidget>
    </div>
  );
}

Hub.propTypes = {
  downloadEvents: PropTypes.func,
  downloadDetails: PropTypes.func,
  getCaps: PropTypes.func,
  cancelGetCaps: PropTypes.func,
  history: PropTypes.shape({
    location: PropTypes.shape({ state: PropTypes.shape({ params: PropTypes.shape({ offset: PropTypes.any }) }) }),
  }),
  onAttachDevice: PropTypes.func,
  onNavigate: PropTypes.func,
  openConfirmModal: PropTypes.func,
  openSendPinModal: PropTypes.func,
  featureConfiguration: PropTypes.func,
  showNotification: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func,
  filters: PropTypes.array.isRequired,
  orgIds: PropTypes.array,
  orgs: PropTypes.array,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
  orgIds: state.entities?.orgFilter?.orgIds,
  orgs: state.entities?.orgFilter?.orgs,
});

const mapDispatchToProps = dispatch => ({
  getCaps: (pageRequest, signal) => dispatch(actions.getCaps(pageRequest, signal)),
  cancelGetCaps: controller => dispatch(actions.cancelGetCaps(controller)),
  onNavigate: (path, params) =>
    dispatch(
      push({
        pathname: path,
        state: { params },
      }),
    ),
  downloadEvents: params => dispatch(actions.downloadReport(params)),
  downloadDetails: params => dispatch(actions.downloadDetails(params)),
  onBack: () => dispatch(goBack()),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  onAttachDevice: postAction =>
    dispatch(
      openModalAction('attach-cap-to-org', {
        postAction,
        type: DeviceTypes.Datahub.id,
      }),
    ),
  openSendPinModal: data => dispatch(openModalAction('send-security-code', data)),
  featureConfiguration: request => dispatch(actions.featureConfiguration(request)),
  showNotification: (message, timeout) => dispatch(notificationActions.show(message, timeout)),
  openDateRangeSelectionModal: (actionOnDone, filters, orgName) =>
    dispatch(openModalAction('date-range-selection-for-events-reports-modal', { actionOnDone, filters, orgName })),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Hub));
